import { z } from 'zod';
import { MKT_TimelineStepStatusSchema, } from './enums';
// ============================================================================
// Address Type (Marketplace-specific shipping address)
// ============================================================================
export const MKT_AddressSchema = z.object({
    street1: z.string().min(1, 'Street address is required'),
    street2: z.string().optional().default(''),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    postalCode: z.string().min(1, 'Postal code is required'),
    country: z.string().min(1, 'Country is required'),
});
// ============================================================================
// Pricing Tier
// ============================================================================
export const MKT_PricingTierSchema = z.object({
    /** Minimum quantity for this price tier */
    minQty: z.number().int().min(1),
    /** Price in cents at this tier */
    priceCents: z.number().int().min(0),
});
// ============================================================================
// Order Timeline Step
// ============================================================================
export const MKT_OrderTimelineStepSchema = z.object({
    /** Step label/description */
    label: z.string(),
    /** Optional timestamp when step occurred */
    date: z.string().optional(),
    /** Step status */
    status: MKT_TimelineStepStatusSchema,
});
// ============================================================================
// Recurring Order Rule
// ============================================================================
export const MKT_RecurringOrderRuleSchema = z.object({
    /** Unique rule identifier */
    id: z.string(),
    /** Type of rule */
    type: z.enum(['min_total', 'item_count']),
    /** Rule threshold value */
    value: z.number(),
    /** Human-readable description */
    description: z.string(),
});
// ============================================================================
// Cart Item (for order creation)
// ============================================================================
export const MKT_CartItemSchema = z.object({
    /** Product ID to order */
    productId: z.string().uuid('Valid product ID required'),
    /** Quantity to order */
    quantity: z.number().int().min(1, 'Quantity must be at least 1'),
});
// ============================================================================
// Pagination
// ============================================================================
export const MKT_PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(10),
});
export const MKT_PaginationMetadataSchema = z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
});
// ============================================================================
// Distributor Types
// ============================================================================
export const MKT_DistributorSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    logoUrl: z.string().nullable(),
});
// ============================================================================
// Business Marketplace Settings
// ============================================================================
/**
 * Zod schema for business marketplace settings.
 * Stored in Business.configurations.marketplaceSettings
 */
export const MKT_BusinessSettingsSchema = z.object({
    /** Array of favorite distributor IDs */
    favoriteDistributorIds: z.array(z.string().uuid()).default([]),
    /** Default shipping address ID */
    defaultShippingAddressId: z.string().uuid().optional(),
});
// ============================================================================
// Sort Order
// ============================================================================
export const MKT_SortOrderSchema = z.enum(['asc', 'desc']).default('asc');
